import { X } from 'lucide-react';
import type { ReactNode } from 'react';
import { t } from '../../i18n';
import { Icon } from '../icons';
import { cn } from '../lib/utils';
import { AlertDescription, Alert as AlertPrimitive, AlertTitle } from '../primitives/alert';
import { Button } from '../primitives/button';
import { ActionButton, type FeedbackAction } from './action';
import { tones, type Tone } from './tones';

type AlertProps = {
  tone: Tone;
  title?: string;
  children?: ReactNode;
  action?: FeedbackAction;
  onDismiss?: () => void;
  sticky?: boolean;
};

// Only danger interrupts a screen reader; the other tones wait their turn.
export function Alert({ tone, title, children, action, onDismiss, sticky = false }: AlertProps) {
  const { icon, className } = tones[tone];
  return (
    <AlertPrimitive
      role={tone === 'danger' ? 'alert' : 'status'}
      className={cn(className, sticky && 'sticky top-0 z-(--z-sticky)', onDismiss && 'pr-12')}
    >
      <Icon icon={icon} size="sm" />
      {title && <AlertTitle>{title}</AlertTitle>}
      <AlertDescription className="text-current">
        {children}
        {action && <ActionButton action={action} variant="outline" />}
      </AlertDescription>
      {onDismiss && (
        <Button
          variant="ghost"
          size="icon"
          aria-label={t('components.Alert.dismiss')}
          className="absolute top-1 right-1"
          onClick={onDismiss}
        >
          <Icon icon={X} size="sm" />
        </Button>
      )}
    </AlertPrimitive>
  );
}
